import { z } from 'zod';
import { MoneySchema, ShortIdSchema, TimestampSchema } from './primitives.js';

export const InvoiceStatusSchema = z.enum([
  'open',
  'paid',
  'past_due',
  'void',
  'refunded',
  'partially_refunded',
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceLineItemKindSchema = z.enum([
  'subscription',
  'extra_outlets',
  'proration_addition',
  'proration_removal',
  'credit',
  'manual_adjustment',
  // Sprint 80 — recruiter epic foundation. First-invoice one-time fees
  // that drive the recruiter + agent commission splits. Materialised
  // from `Plan.fees` on the operator's first generated invoice.
  'signup_fee',
  'activation_fee',
  // Sprint 91 — Malaysia SST 8% line item (or country equivalent).
  // Added by monthlyBilling when the PLATFORM (the supplier) is SST-registered
  // — `platformConfig/moneyPolicy.supplierTax`, never the operator's own status.
  // Computed on the sum of all SST-applicable lines (subscription,
  // extra_outlets, proration_addition, signup_fee, activation_fee);
  // never compounds on itself. Agent/recruiter commission helpers
  // ignore this kind so SST stays government-bound, not earnings-bound.
  'sst',
]);
export type InvoiceLineItemKind = z.infer<typeof InvoiceLineItemKindSchema>;

/**
 * How an invoice was settled. `toyyibpay` is the live automated FPX rail
 * (ADR-0022 Option B — `toyyibPayInvoiceWebhook` stamps it); `billplz` is the
 * retired predecessor, kept for historical rows. The remaining values are
 * recorded by the super-admin manual mark-paid path (ADR-0022 Option C, the
 * fallback): an operator pays by bank transfer / DuitNow and a super-admin
 * records it, `cash` for over-the-counter, `waived` when Kilat writes the
 * balance off. Only set once an invoice reaches `paid`.
 */
export const InvoicePaymentMethodSchema = z.enum([
  'billplz',
  'toyyibpay',
  'manual_bank_transfer',
  'cash',
  'waived',
]);
export type InvoicePaymentMethod = z.infer<typeof InvoicePaymentMethodSchema>;

/** The subset of payment methods a human super-admin may record manually. */
export const ManualInvoicePaymentMethodSchema = z.enum(['manual_bank_transfer', 'cash', 'waived']);
export type ManualInvoicePaymentMethod = z.infer<typeof ManualInvoicePaymentMethodSchema>;

export const InvoiceLineItemSchema = z.object({
  kind: InvoiceLineItemKindSchema,
  description: z.string(),
  quantity: z.number().int().positive().optional(),
  unitPrice: MoneySchema.optional(),
  amount: z.number().int().describe('Signed amount in sen; can be negative for credits'),
  meta: z.record(z.string(), z.unknown()).optional(),
});
export type InvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;

export const InvoiceSchema = z.object({
  id: ShortIdSchema,
  periodStart: TimestampSchema,
  periodEnd: TimestampSchema,
  lineItems: z.array(InvoiceLineItemSchema).min(1),
  subtotal: MoneySchema,
  tax: MoneySchema.default(0),
  total: MoneySchema,
  status: InvoiceStatusSchema,
  dueDate: TimestampSchema,
  paymentLink: z.string().url().optional(),
  paymentRef: z.string().optional().describe('Billplz bill ID or HitPay reference'),
  paymentMethod: InvoicePaymentMethodSchema.optional().describe(
    'How the invoice was settled. Set together with paidAt when status → paid.',
  ),
  paidBy: z
    .string()
    .optional()
    .describe(
      'Actor who recorded the payment. Super-admin uid for a manual mark-paid (ADR-0022 Option C); absent for automated gateway settlement.',
    ),
  paymentNote: z
    .string()
    .max(500)
    .optional()
    .describe('Free-text reference/note captured when a payment is recorded manually.'),
  supplierSst: z
    .object({
      registered: z.boolean(),
      sstNumber: z.string().optional(),
      rate: z.number().min(0).max(1),
    })
    .optional()
    .describe(
      "Snapshot of the platform's SST status (the SUPPLIER) when this invoice was issued, so a " +
        'PDF rendered later says what was true at issue time. Absent on invoices issued before ' +
        '2026-10.',
    ),
  paidAt: TimestampSchema.optional(),
  refundedAt: TimestampSchema.optional(),
  refundedAmount: MoneySchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Invoice = z.infer<typeof InvoiceSchema>;
